import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { Box, Collapse, Paper, Typography } from '@mui/material'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { BadgeTile } from './BadgeTile'
import { BADGE_GAP, BADGE_SIZE, VISIBLE_BADGE_POSITIONS } from './constants'

/**
 * One multi-level designation (AC4, AC5, AC6).
 *
 * Collapsed, it is the level the donor currently holds. Expanded, it is every
 * level they have earned in order, with the next one locked at the end.
 *
 * The rail shows four positions and scrolls (AC5). Nothing is rendered past the
 * locked badge, which is what stops the donor scrolling forward into levels
 * they have not reached - there is nothing there to scroll to.
 */
export const MilestoneCollection = ({ collection }) => {
  const [expanded, setExpanded] = useState(false)
  const railRef = useRef(null)

  const {
    name,
    current_level_name,
    current_level_badge_image_url,
    badges_earned,
    total_credits,
    levels = [],
    next_level,
  } = collection

  const tiles = [
    ...levels.map(level => ({ ...level, locked: false })),
    ...(next_level ? [{ ...next_level, locked: true }] : []),
  ]

  /** AC5: the newest badges and the locked one, not level one. */
  const scrollToEnd = useCallback(() => {
    const rail = railRef.current
    if (rail) rail.scrollLeft = rail.scrollWidth
  }, [])

  /**
   * Before paint, so the rail is never seen sitting at the first level. It runs
   * again from Collapse's onEntered because mid-animation the rail has not
   * reached its full scrollWidth yet and this pass lands short.
   */
  useLayoutEffect(() => {
    if (expanded) scrollToEnd()
  }, [expanded, tiles.length, scrollToEnd])

  // Four positions is the ceiling (AC5), not a fixed width: a quarter of a
  // phone-width rail is narrower than the badge itself, so the tile stops
  // shrinking at a size the artwork still fits and the rail scrolls instead.
  const tileWidth = `clamp(140px, calc((100% - ${
    (VISIBLE_BADGE_POSITIONS - 1) * BADGE_GAP
  }px) / ${VISIBLE_BADGE_POSITIONS}), 260px)`

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        component='button'
        type='button'
        onClick={() => setExpanded(open => !open)}
        aria-expanded={expanded}
        aria-label={`${name} badges`}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {current_level_badge_image_url ? (
          <Box
            component='img'
            src={current_level_badge_image_url}
            alt=''
            sx={{
              width: BADGE_SIZE * 0.6,
              height: BADGE_SIZE * 0.6,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: BADGE_SIZE * 0.6,
              height: BADGE_SIZE * 0.6,
              borderRadius: '50%',
              backgroundColor: 'secondary.main',
              flexShrink: 0,
            }}
          />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* The designation names the collection: it is what the row is, and
              what stays the same as the donor climbs it. The level they hold is
              the badge beside it and is named on the line below, where "Gold"
              on its own would not have said which programme it belonged to. */}
          <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {name}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {current_level_name}
            {'  ·  '}
            {badges_earned} {badges_earned === 1 ? 'badge' : 'badges'} earned
            {/* AC8: the credit total is absent, not zeroed, when hidden. */}
            {total_credits != null && (
              <Box
                component='span'
                sx={{ color: 'primary.main', fontWeight: 700 }}
              >
                {'  ·  '}
                {total_credits.toLocaleString()} total credits
              </Box>
            )}
          </Typography>
        </Box>

        <Box sx={{ color: 'text.secondary', display: 'flex' }}>
          {expanded ? (
            <ExpandLessRoundedIcon />
          ) : tiles.length ? (
            <ExpandMoreRoundedIcon />
          ) : (
            <ChevronRightRoundedIcon />
          )}
        </Box>
      </Box>

      <Collapse in={expanded} unmountOnExit onEntered={scrollToEnd}>
        <Box
          ref={railRef}
          sx={{
            display: 'flex',
            gap: `${BADGE_GAP}px`,
            px: 2,
            pb: 2,
            overflowX: 'auto',
            scrollbarWidth: 'thin',
          }}
        >
          {tiles.map(tile => (
            <BadgeTile
              key={tile.level_id}
              name={tile.name}
              imageUrl={tile.badge_image_url}
              locked={tile.locked}
              creditsRequired={tile.credits_required}
              creditsNeeded={tile.locked ? tile.credits_needed : null}
              width={tileWidth}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  )
}
